import { API_BASE, authHeaders } from './apiAuth'
import type { ImageRef } from './imageRefs'

/**
 * Making pictures
 * -------------------------------------------------------------
 * A run is started and then asked about, rather than waited on. One 1024
 * picture at low quality takes about eight seconds; ten at 4K is minutes, and
 * a browser holding a connection open that long is a bet that ends with the
 * money spent and the pictures lost.
 *
 * WHAT A MODEL CAN DO IS DATA
 * Sizes, qualities, how many at once — all of it comes from the server, per
 * model, because it differs per model: one renders 4K and the others do not.
 * Written into this file instead, the card would offer 4K on a model that
 * refuses it, or worse, on one that accepts the request and bills for it.
 */

/** A US dollar is a thousand credits. One low 1024 picture is about six. */
export const CREDITS_PER_USD = 1000

export interface Choice {
  value: string
  label: string
}

/**
 * One size a model will actually take.
 *
 * `value` is what goes on the wire — real pixels, from the model's own list.
 * `w` and `h` are the shape in its simplest terms, and only so the card can
 * draw a little rectangle; they are both zero for 'auto', which has no shape.
 *
 * Not computed from a ratio and a resolution, which is what it was for a day:
 * 16:9 at 1K came out as 1024x576, and the API refuses that as below its
 * minimum pixel budget. A list cannot express a combination nobody offers.
 */
export interface PictureSize {
  value: string
  label: string
  w: number
  h: number
  /**
   * For a model that is chosen by ratio AND resolution rather than by one
   * list -- Nano Banana 2 and Pro. `16:9`, and `1K` / `2K` / `4K`. Absent on
   * the OpenAI models, whose sizes are one flat list.
   *
   * `value` stays the pixels either way (`2752x1536`), so everything that
   * already reads a size -- the price band, the card's shape, the pixel limit,
   * the server's check against the list -- goes on working without knowing
   * which kind of model it is looking at.
   */
  aspect?: string
  resolution?: string
}

/** True for a model picked by resolution and ratio (Nano Banana), not one list. */
export function hasResolutions(model: PictureModel | undefined): boolean {
  return Boolean(model?.sizes.some((one) => one.resolution))
}

/** A model's resolutions, in the order its sizes list them. */
export function resolutionsOf(model: PictureModel | undefined): string[] {
  const seen: string[] = []
  for (const one of model?.sizes ?? []) {
    if (one.resolution && !seen.includes(one.resolution)) seen.push(one.resolution)
  }
  return seen
}

/**
 * A size this model actually offers.
 *
 * The one asked for if it is on the list; otherwise `auto` if the model has
 * it, otherwise the model's first size. Nano Banana has no `auto`, so the
 * `auto` every page starts on -- and falls back to on switching model --
 * would otherwise be sent, and refused, as a size it never offered.
 */
export function sizeFor(model: PictureModel | undefined, size: string): string {
  if (!model || model.sizes.length === 0) return size
  if (model.sizes.some((one) => one.value === size)) return size
  return model.sizes.find((one) => one.value === 'auto')?.value ?? model.sizes[0]!.value
}

/** The resolution a size is at, for a model picked that way. */
export function resolutionOf(model: PictureModel | undefined, size: string): string {
  const sizes = model?.sizes ?? []
  return sizes.find((one) => one.value === size)?.resolution ?? sizes[0]?.resolution ?? ''
}

/**
 * The ratio tiles to show.
 *
 * Every size, for a model with one flat list. For one picked by resolution,
 * only the ratios at the resolution now chosen -- the same ten shapes, once.
 */
export function shapesFor(model: PictureModel | undefined, size: string): PictureSize[] {
  if (!model) return []
  if (!hasResolutions(model)) return model.sizes
  const at = resolutionOf(model, size)
  return model.sizes.filter((one) => one.resolution === at)
}

/**
 * The same ratio at another resolution.
 *
 * Changing 2K to 4K should keep the 16:9 that was chosen, not jump back to
 * the first shape on the list.
 */
export function atResolution(model: PictureModel, size: string, resolution: string): string {
  const now = model.sizes.find((one) => one.value === size)
  const same = model.sizes.find(
    (one) => one.resolution === resolution && one.aspect === now?.aspect,
  )
  return (same ?? model.sizes.find((one) => one.resolution === resolution))?.value ?? size
}

export interface PictureModel {
  slug: string
  label: string
  note: string
  sizes: PictureSize[]
  qualities: Choice[]
  maxImages: number
  maxReferences: number
  /**
   * Output tokens per picture, by quality. The flat table: what an older
   * config carries, and the fallback for a size band with no row of its own.
   */
  outputTokens: Record<string, number>
  /**
   * Output tokens per picture by SIZE BAND, then quality -- `1k`, `2k`, `4k`.
   * Medium and high scale with the pixels; see `estimateCredits`.
   */
  outputTokensBySize: Record<string, Record<string, number>>
  /**
   * Input tokens one reference picture costs, FOR EACH PICTURE MADE. An
   * allowance, not a rate.
   */
  referenceTokens: number
  /** US dollars per million output tokens. */
  outputUsdPerMtok: number
  /** US dollars per million image-input tokens, which is what references are. */
  inputUsdPerMtok: number
}

export interface PictureRun {
  id: string
  /** How many runs this person has going, anywhere, and the ceiling. */
  live: number
  limit: number
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  outputs: ImageRef[]
  pictures: { id: string; store: 'library' | 'project'; originalPath: string }[]
  error: string
  costUsd: number
  credits: number
}

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    return body.error || `Request failed (${response.status})`
  } catch {
    return `Request failed (${response.status})`
  }
}

function choices(raw: unknown): Choice[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((one) => one as { value?: unknown; label?: unknown })
    .filter((one) => typeof one.value === 'string')
    .map((one) => ({ value: String(one.value), label: String(one.label ?? one.value) }))
}

/**
 * Which models can make pictures, and what each one offers.
 *
 * Everything numeric goes through Number(), because these arrive as JSON from
 * a database column and a string that looks like a number would otherwise turn
 * a price estimate into "8.0030" by concatenation — the kind of wrong that
 * displays perfectly well.
 */
export async function pictureModels(): Promise<PictureModel[]> {
  const response = await fetch(`${API_BASE}/api/ai/models?kind=image`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await reason(response))
  const rows = (await response.json()) as Record<string, unknown>[]

  return rows.map((row) => {
    const config = (row.config ?? {}) as Record<string, unknown>
    const perQuality = (config.outputTokens ?? {}) as Record<string, unknown>
    return {
      slug: String(row.slug ?? ''),
      label: String(row.label ?? row.slug ?? ''),
      note: String(row.note ?? ''),
      sizes: Array.isArray(config.sizes)
        ? (config.sizes as Record<string, unknown>[])
            .filter((one) => typeof one.value === 'string')
            .map((one) => ({
              value: String(one.value),
              label: String(one.label ?? one.value),
              w: Number(one.w) || 0,
              h: Number(one.h) || 0,
              ...(typeof one.aspect === 'string' ? { aspect: one.aspect } : {}),
              ...(typeof one.resolution === 'string' ? { resolution: one.resolution } : {}),
            }))
        : [],
      qualities: choices(config.qualities),
      maxImages: Math.max(1, Number(config.maxImages) || 1),
      maxReferences: Math.max(0, Number(config.maxReferences) || 0),
      outputTokens: Object.fromEntries(
        Object.entries(perQuality).map(([key, value]) => [key, Number(value) || 0]),
      ),
      outputTokensBySize: Object.fromEntries(
        Object.entries((config.outputTokensBySize ?? {}) as Record<string, unknown>).map(
          ([band, table]) => [
            band,
            Object.fromEntries(
              Object.entries((table ?? {}) as Record<string, unknown>).map(([key, value]) => [
                key,
                Number(value) || 0,
              ]),
            ),
          ],
        ),
      ),
      referenceTokens: Number(config.referenceTokens) || 0,
      outputUsdPerMtok: Number(row.output_usd_per_mtok) || 0,
      inputUsdPerMtok: Number(row.input_usd_per_mtok) || 0,
    }
  })
}

/**
 * Which size band a size is in, by its pixels.
 *
 *   1k   up to 2 MP     1024x1024, 1536x1024, 1024x1536 -- and `auto`
 *   2k   up to 5 MP     2560x1440
 *   4k   above that     3840x2160
 *
 * `auto` goes in 1k because that is what the model has picked every time it
 * was measured: a high picture at auto size came out at 1,801 tokens, the
 * 1K figure, not the 4K one.
 */
export function sizeBand(size: string): '1k' | '2k' | '4k' {
  const match = /^(\d+)x(\d+)$/.exec(size)
  if (!match) return '1k'
  const pixels = Number(match[1]) * Number(match[2])
  if (pixels <= 2_000_000) return '1k'
  if (pixels <= 5_000_000) return '2k'
  return '4k'
}

/**
 * What a run will probably cost, in credits, before it is started.
 *
 * REWRITTEN 2026-09-28 FROM THE WHOLE HISTORY, NOT FROM A FEW PROBES
 *
 * `docs/pricing-history.sql` read back every picture run there has been, and
 * the quote was wrong in three ways, two of them in the direction that
 * misleads.
 *
 * 1. REFERENCES ARE BILLED ONCE PER PICTURE MADE, not once per request. Four
 *    pictures from three references is twelve references on the bill --
 *    18,428 input tokens, 4 x 3 x 1,536. Quoted 61, cost 161 to 208. The
 *    allowance of 1,500 a reference was right all along; the multiplier was
 *    missing. (The comment here used to say the opposite, confidently.)
 *
 * 2. SIZE DOES MOVE THE PRICE -- above low. Low is nearly flat (196 at 1K,
 *    205 at 2.5K), which is where "flat in size" was measured and why it was
 *    believed. Medium goes 1,372 / 1,843 / 3,336 across 1K / 2.5K / 4K, and
 *    high goes 1,801 at 1K to 13,342 at 4K. Hence a table by size band.
 *
 * 3. AUTO IS NOT LOW. The model picks, and across 24 measured pictures it
 *    picked anything from 129 to 1,372 tokens. It is quoted at the middle of
 *    that -- 450 at 1K -- by Huanrui's decision, knowing that means about a
 *    third of auto runs cost somewhat more than they said. Quoting the top
 *    would have been never wrong and usually double. At 2K and 4K it keeps
 *    the same share of medium (600, 1,100) -- scaled, not measured.
 *
 * Prompt text is left out: about 48 tokens a picture at $5 a million, a
 * quarter of a credit.
 */
export function estimateCredits(
  model: PictureModel,
  quality: string,
  size: string,
  n: number,
  references: number,
): number {
  const pictures = Math.max(1, n)
  const band = model.outputTokensBySize[sizeBand(size)] ?? {}
  const perPicture =
    band[quality] ?? model.outputTokens[quality] ?? band.auto ?? model.outputTokens.auto ?? 0
  const output = perPicture * pictures * model.outputUsdPerMtok
  // Once for every picture made -- see 1 above.
  const input = references * pictures * model.referenceTokens * model.inputUsdPerMtok
  const usd = (output + input) / 1_000_000
  if (usd <= 0) return 0
  return Math.max(1, Math.ceil(usd * CREDITS_PER_USD))
}

/**
 * The Miro panel's placeholders, handed to the server to fill when the run is
 * done (2026-10-14, workers/api/src/boardPlacement.ts): the board, and one
 * placeholder id per picture in order, null where none was drawn.
 */
export interface BoardHandover {
  boardId: string
  items: (string | null)[]
}

export interface PictureAsk {
  projectId: string
  prompt: string
  model: string
  size: string
  quality: string
  n: number
  /** Reference pictures, in the order they are wired down the side of the card. */
  references: ImageRef[]
  board?: BoardHandover
}

export interface Started {
  runId: string
  /** Including this one, and the ceiling across every surface. */
  live: number
  limit: number
  /** The server puts the pictures on the board; the panel must not. */
  serverPlaces: boolean
}

/** Start a run. Comes back as soon as it is written down, not when it is done. */
export async function startPictures(ask: PictureAsk): Promise<Started> {
  const response = await fetch(`${API_BASE}/api/skills/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({
      skill: 'skill-image-generation',
      projectId: ask.projectId,
      inputs: ask.references,
      params: {
        prompt: ask.prompt,
        model: ask.model,
        size: ask.size,
        quality: ask.quality,
        n: ask.n,
        ...(ask.board ? { board: ask.board } : {}),
      },
    }),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { runId?: string; live?: number; limit?: number; board?: string }
  if (!body.runId) throw new Error('The run did not start')
  return {
    runId: body.runId,
    live: Number(body.live) || 0,
    limit: Number(body.limit) || 0,
    serverPlaces: body.board === 'server',
  }
}

/**
 * The board's Edit area (Worker: routes/canvasEdit.ts). The picture and its
 * mask are project files the board has just uploaded; the model, quality and
 * size are the Worker's decision. Waited on with awaitRun.
 */
export const CANVAS_EDIT_MODEL = 'gpt-image-2.5-sunburst'
export const CANVAS_EDIT_QUALITY = 'high'

export async function startCanvasEdit(ask: {
  projectId: string
  /** 'text' is Edit text: `changes` and `keep` instead of a prompt. */
  kind: 'edit' | 'text'
  /** The part of the picture round the box, as it is. */
  image: ImageRef
  /** More pictures for the model to look at, in port order. */
  references: ImageRef[]
  size: string
  prompt: string
  changes?: { from: string; to: string; where?: string }[]
  keep?: string[]
}): Promise<Started> {
  const response = await fetch(`${API_BASE}/api/canvas-edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(ask),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { runId?: string; live?: number; limit?: number }
  if (!body.runId) throw new Error('The run did not start')
  // Board edits are put together in the browser; the server never places them.
  return { runId: body.runId, live: Number(body.live) || 0, limit: Number(body.limit) || 0, serverPlaces: false }
}

/** A line of text found on a picture, its box as fractions of the picture (0 to 1). */
export interface FoundText {
  text: string
  box: { x: number; y: number; w: number; h: number }
}

/**
 * Edit text, step one (workers/api/src/routes/canvasText.ts): the lines of
 * text on a picture the board has just uploaded, read there and then.
 */
export async function detectCanvasText(ask: {
  projectId: string
  image: ImageRef
}): Promise<{ lines: FoundText[]; credits: number }> {
  const response = await fetch(`${API_BASE}/api/canvas-text/detect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(ask),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { lines?: FoundText[]; credits?: number }
  return { lines: Array.isArray(body.lines) ? body.lines : [], credits: Number(body.credits) || 0 }
}

export async function runStatus(runId: string): Promise<PictureRun> {
  const response = await fetch(
    `${API_BASE}/api/skills/run?id=${encodeURIComponent(runId)}`,
    { headers: await authHeaders() },
  )
  if (!response.ok) throw new Error(await reason(response))
  return shape(await response.json(), runId)
}

/**
 * One answer about a run, read defensively.
 *
 * Shared by the poll and by the work request because both are told about a run
 * in the same shape, and two readers of one shape is two chances for them to
 * drift. Every number goes through Number(): these arrive as JSON out of a
 * numeric database column, and a string that looks like a number turns an
 * addition into a concatenation — which displays perfectly well and is wrong.
 */
function shape(raw: unknown, runId: string): PictureRun {
  const body = (raw ?? {}) as Record<string, unknown>
  const status = String(body.status ?? 'queued')
  return {
    id: String(body.id ?? runId),
    live: Number(body.live) || 0,
    limit: Number(body.limit) || 0,
    status:
      status === 'running' || status === 'succeeded' || status === 'failed' ? status : 'queued',
    outputs: decodeRefs(body.outputs),
    pictures: Array.isArray(body.pictures)
      ? (body.pictures as Record<string, unknown>[]).map((one) => ({
          id: String(one.id ?? ''),
          store: one.store === 'library' ? 'library' : 'project',
          originalPath: String(one.originalPath ?? ''),
        }))
      : [],
    error: String(body.error ?? ''),
    costUsd: Number(body.costUsd) || 0,
    credits: Number(body.credits) || 0,
  }
}

function decodeRefs(raw: unknown): ImageRef[] {
  if (!Array.isArray(raw)) return []
  const refs: ImageRef[] = []
  for (const one of raw) {
    if (typeof one !== 'string') continue
    const colon = one.indexOf(':')
    if (colon < 0) continue
    const store = one.slice(0, colon)
    if (store !== 'library' && store !== 'project') continue
    refs.push({ store, id: one.slice(colon + 1) })
  }
  return refs
}

/** Give up after this long. Past the server's own ten-minute sweep. */
const GIVE_UP_AFTER = 12 * 60 * 1000
/** Consecutive failed polls before the asking is treated as the problem. */
const MAX_MISSES = 5
/** Between polls. The first answer never comes quickly. */
const EVERY = 2500

/**
 * Start to finish, by asking rather than by waiting.
 *
 * WHY THIS IS A POLL AND NOT ONE LONG REQUEST
 *
 * It was one long request for a day. Cloudflare's edge cuts a request at about
 * a hundred seconds — measured: a run finished on the server in 104 seconds
 * with the picture stored and the money spent, and the browser was told the
 * connection had been lost. Before that the work lived in `ctx.waitUntil`,
 * which gets about thirty seconds and is cancelled with no trace at all.
 *
 * The work is on a queue now, so nothing here has to stay alive for it. This
 * only asks how it is going, and the answer outlives the asking: close the tab
 * and the pictures still arrive, they are simply on the board next time.
 *
 * IT HAS TO BE ABLE TO GIVE UP
 *
 * A poll that failed used to log a warning and ask again for ever, so a broken
 * connection and a run still working looked identical — "Making…" until the
 * page was reloaded. Five misses in a row means the asking is broken; twelve
 * minutes means the server's own sweep has already been past. Either way the
 * card is told, in words, that nobody knows.
 */
export async function awaitRun(runId: string, signal?: AbortSignal): Promise<PictureRun> {
  const startedAt = Date.now()
  let misses = 0

  for (;;) {
    await new Promise((wake) => setTimeout(wake, EVERY))

    // Whoever was waiting has gone -- the page moved to another conversation.
    // Asking on for twelve minutes would be traffic nobody reads. The run
    // itself is untouched; this only stops watching it.
    if (signal?.aborted) return dead(runId, 'Stopped watching this run')

    try {
      const run = await runStatus(runId)
      misses = 0
      if (run.status === 'succeeded' || run.status === 'failed') return run
    } catch (error) {
      // One failed poll is not a failed run — the network dropped, or the
      // Worker was busy. Several in a row is a different thing.
      misses += 1
      console.warn('could not read the run', error)
      if (misses >= MAX_MISSES) {
        return dead(runId, 'Lost track of this run — reload the board to see if it finished')
      }
    }

    if (Date.now() - startedAt > GIVE_UP_AFTER) {
      return dead(runId, 'This took longer than it should have — reload the board to see if it finished')
    }
  }
}

function dead(runId: string, why: string): PictureRun {
  return {
    id: runId,
    status: 'failed',
    live: 0,
    limit: 0,
    outputs: [],
    pictures: [],
    error: why,
    costUsd: 0,
    credits: 0,
  }
}

/* ------------------------------------------------------------------ *
 * The page that is not a board
 * ------------------------------------------------------------------ */

/**
 * Where work that does not belong to a project lives.
 *
 * One hidden project each, made the first time somebody opens the image
 * generation page. Everything it needs — storing a picture, signing a link,
 * naming it, billing it, finding it again — already exists and is keyed on a
 * project, so this reuses all of it rather than growing a parallel set with a
 * third spelling of what a picture is called.
 */
export async function personalProject(): Promise<string> {
  const response = await fetch(`${API_BASE}/api/projects/personal`, {
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { id?: string }
  if (!body.id) throw new Error('No personal project')
  return body.id
}

/** One picture in a run, either something that went in or something made. */
export interface RunPicture {
  id: string
  store: 'library' | 'project'
  originalPath: string
  title: string
}

export interface HistoryRun {
  id: string
  status: PictureRun['status']
  error: string
  credits: number
  /** What it was made with: prompt, model, size, quality, how many. */
  params: Record<string, unknown>
  /**
   * The references that went in, as names (`project:<id>`).
   *
   * Kept because it is the only record of which pictures have ever been used
   * as a reference — which is what the generate page's plus button offers.
   */
  inputs: string[]
  createdAt: string
  pictures: RunPicture[]
  /** The references it was made from, resolved. */
  references: RunPicture[]
}

/**
 * Everything this project has made, newest first.
 *
 * With the prompt and the settings still attached to each one, which is the
 * point: a picture on its own cannot be repeated, argued with or explained to
 * anybody a week later.
 */
/**
 * Throw a run away, with the pictures it made.
 *
 * Its references are left alone — they were uploaded on purpose and are
 * almost always used again.
 */
export async function forgetRun(runId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE}/api/skills/runs?run=${encodeURIComponent(runId)}`,
    { method: 'DELETE', headers: { Accept: 'application/json', ...(await authHeaders()) } },
  )
  if (!response.ok) throw new Error(await reason(response))
}

function asPictures(value: unknown): RunPicture[] {
  if (!Array.isArray(value)) return []
  return (value as Record<string, unknown>[]).map((one) => ({
    id: String(one.id ?? ''),
    store: one.store === 'library' ? ('library' as const) : ('project' as const),
    originalPath: String(one.originalPath ?? ''),
    title: String(one.title ?? ''),
  }))
}

export async function listRuns(projectId: string): Promise<HistoryRun[]> {
  const response = await fetch(
    `${API_BASE}/api/skills/runs?project=${encodeURIComponent(projectId)}`,
    { headers: { Accept: 'application/json', ...(await authHeaders()) } },
  )
  if (!response.ok) throw new Error(await reason(response))
  const rows = (await response.json()) as Record<string, unknown>[]
  return rows.map((row) => ({
    id: String(row.id ?? ''),
    status: (String(row.status ?? 'queued') as PictureRun['status']) ?? 'queued',
    error: String(row.error ?? ''),
    credits: Number(row.credits) || 0,
    params: (row.params ?? {}) as Record<string, unknown>,
    inputs: Array.isArray(row.inputs) ? row.inputs.map((one) => String(one)) : [],
    createdAt: String(row.createdAt ?? ''),
    pictures: asPictures(row.pictures),
    references: asPictures(row.references),
  }))
}
